import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { MirrorService } from 'services/MirrorService';

import { OneClickPaymentResponse } from 'api/PaymentApi';
import { OrderType } from 'api/TrackingApi';
import { CreditsFunnels, getFunnelSearchString } from 'helpers/funnels/funnels';
import { assertString } from 'helpers/utils/checkType';
import { useCreditsAnalytics } from 'hooks/analytics/useCreditsAnalytics';
import { useOneClickPayAnalytics } from 'hooks/analytics/useOneClickPayAnalytics';
import { useCreditPayMutation } from 'hooks/mutations/useCreditPayMutation';
import { useCreditResignMutation } from 'hooks/mutations/useCreditResignMutation';
import { useCreditsRefillStatusMutation } from 'hooks/mutations/useCreditsRefillStatusMutation';
import {
  useCreditsRefillStatusQuery,
  useCreditsRefillStatusQueryCache,
} from 'hooks/queries/useCreditsRefillStatusQuery';
import { useOneClickPayment } from 'hooks/useOneClickPayment';
import { usePackPrice } from 'hooks/usePackagePrice';
import {
  oneClickPaymentReset,
  setOneClickPaymentVerifyUrl,
} from 'store/oneClickPayment/oneClickPaymentSlice';
import {
  getOneClickPaymentOrderId,
  getOneClickPaymentVerifyUrl,
} from 'store/oneClickPayment/selectors';
import { addCreditsAmount } from 'store/payment/paymentSlice';
import {
  getCreditsCurrencySelector,
  getOptimalCreditPackageSelector,
} from 'store/payment/selectors';
import {
  setActiveSystemPopup,
  SystemPopupTypes,
} from 'store/systemPopup/systemPopupSlice';

import { ReactComponent as HeartIcon } from 'assets/icons/heart.svg';

import { BaseButton, ButtonTypes } from 'components/base/BaseButton';
import { Checkbox } from 'components/base/Checkbox';
import { CardMask } from 'components/shared/CardMask';
import { CvvPopup } from 'components/shared/CvvPopup';
import { DefaultPaymentFailPopup } from 'components/shared/DefaultPaymentFailPopup';
import { InsufficientPaymentFailPopup } from 'components/shared/InsufficientPaymentFailPopup';
import { Payment3dsPopup } from 'components/shared/Payment3dsPopup';
import { ImagedPopup } from 'components/UI/ImagedPopup';

import thumbnailImg from './images/thumbnail.jpg';

import css from './creditsInsufficientPopup.module.sass';

enum PaymentFailPopupType {
  Default = 1,
  Insufficient,
}

interface Props {
  params: {
    funnel: CreditsFunnels;
    balance?: number;
    cost?: number;
    successEvent?: {
      name: string;
      data?: any;
    };
    redirectOnClosClick?: () => void;
  };
  onClose: () => void;
}

export const CreditsInsufficientPopup: React.FC<Props> = ({
  params,
  onClose,
}) => {
  const { t } = useTranslation(['app', 'common']);
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const creditPackage = useSelector(
    getOptimalCreditPackageSelector(params.balance, params.cost)
  );
  const orderId = useSelector(getOneClickPaymentOrderId);
  const verifyUrl = useSelector(getOneClickPaymentVerifyUrl);
  const creditsCurrency = useSelector(getCreditsCurrencySelector);

  const creditsRefillStatusQuery = useCreditsRefillStatusQuery();
  const creditsRefillStatusQueryCache = useCreditsRefillStatusQueryCache();
  const creditsRefillStatusMutation = useCreditsRefillStatusMutation();

  const {
    trackCreditReplenishChecked,
    trackVerticaCreditsNotEnough,
    trackVerticaCreditsPurchaseFail,
  } = useCreditsAnalytics();

  const creditPayMutation = useCreditPayMutation();
  const creditResignMutation = useCreditResignMutation();

  const { cardMask, isProcess, handleRecurrentSubmit, handleResignSubmit } =
    useOneClickPayment({
      recurrentMutation: creditPayMutation,
      resignMutation: creditResignMutation,
    });

  const { trackResignShow } = useOneClickPayAnalytics({
    orderType: OrderType.Credit,
  });
  const [isCvvVisible, setIsCvvVisible] = useState(false);
  const [visiblePaymentFailPopup, setVisiblePaymentFailPopup] =
    useState<PaymentFailPopupType | null>(null);
  const shouldReplaceUrlAtUnmount = useRef(true);

  const isCardBlocked = useMemo(
    () => creditPayMutation?.error?.response?.data?.code === 'card_blocked',
    [creditPayMutation]
  );

  const isSubmitting = isProcess;
  const packagePrice = usePackPrice({ package: creditPackage });
  const creditsBuyPage = `/credit-upgrade?${getFunnelSearchString(
    params.funnel
  )}`;

  const handleCloseClick = () => {
    trackVerticaCreditsNotEnough({
      replenish: creditsRefillStatusQuery.data?.enabled ? 'on' : 'off',
      operation: 'close',
    });

    if (params.redirectOnClosClick) {
      shouldReplaceUrlAtUnmount.current = false;
      params.redirectOnClosClick();
    }

    onClose();
  };

  const handleChangePackage = () => {
    shouldReplaceUrlAtUnmount.current = false;
    history.push(creditsBuyPage, { successEvent: params.successEvent });
    onClose();
  };

  const handleSuccessPayment = () => {
    dispatch(
      setActiveSystemPopup({
        type: SystemPopupTypes.CreditsPurcased,
        params: {
          creditsAmount: creditPackage?.credits_buy,
          successEvent: params.successEvent,
        },
      })
    );

    if (creditPackage) {
      dispatch(addCreditsAmount(creditPackage?.credits_buy));
    }
  };

  const handlePaymentFail = () => {
    setVisiblePaymentFailPopup(PaymentFailPopupType.Default);
  };

  const handleInsufficientPaymentFail = () => {
    setVisiblePaymentFailPopup(PaymentFailPopupType.Insufficient);
  };

  const handle3dsVerification = (data: OneClickPaymentResponse) => {
    dispatch(setOneClickPaymentVerifyUrl(data.verify_url));
  };

  const handleCloseCvvPopup = () => {
    setIsCvvVisible(false);
  };

  const handleShowCvvPopup = (data: OneClickPaymentResponse) => {
    setIsCvvVisible(true);

    trackResignShow(data.order.id);
  };

  const handlePay = () => {
    if (creditPackage) {
      handleRecurrentSubmit({
        packageId: creditPackage.id,
        onSuccessPayment: handleSuccessPayment,
        onRetryablePaymentFail: handleShowCvvPopup,
        on3dsVerifyPaymentFail: handle3dsVerification,
        onInsufficientFundsPaymentFail: handleInsufficientPaymentFail,
        onFinalPaymentFail: handlePaymentFail,
      });
    }

    trackVerticaCreditsNotEnough({
      replenish: creditsRefillStatusQuery.data?.enabled ? 'on' : 'off',
      operation: 'click',
    });
  };

  const handleResign = ({ cvv }: { cvv?: string }) => {
    if (creditPackage?.id && assertString(cvv)) {
      handleResignSubmit({
        packageId: creditPackage.id,
        cvv,
        onSuccessPayment: handleSuccessPayment,
        on3dsVerifyPaymentFail: handle3dsVerification,
        onInsufficientFundsPaymentFail: handleInsufficientPaymentFail,
        onFinalPaymentFail: handlePaymentFail,
      });
    }
  };

  const handlePaymentFailPopupMount = () => {
    if (orderId) {
      trackVerticaCreditsPurchaseFail({
        orderId,
        operation: 'show',
      });
    }
  };

  const handleReplenishChecked = (e: any) => {
    creditsRefillStatusMutation.mutate(
      {
        enabled: Boolean(e.target.checked),
        refillPackageInfo: creditPackage?.id,
      },
      {
        onSuccess: (data) => {
          creditsRefillStatusQueryCache.setCreditsRefillStatus(data.enabled);
        },
      }
    );

    trackCreditReplenishChecked({
      status: e.target.checked ? 'on' : 'off',
      creditFunnel: params.funnel,
    });

    trackVerticaCreditsNotEnough({
      replenish: e.target.checked ? 'on' : 'off',
      operation: 'click',
    });
  };

  const handleChangeCreditCard = () => {
    shouldReplaceUrlAtUnmount.current = false;
    if (orderId) {
      trackVerticaCreditsPurchaseFail({
        orderId,
        operation: 'click',
      });
    }

    history.push(`/payments_credit/${creditPackage?.id}${location.search}`);
    onClose();
  };

  const handlePaymentFailPopupCloseClick = () => {
    if (orderId) {
      trackVerticaCreditsPurchaseFail({
        orderId,
        operation: 'close',
      });
    }

    if (params.redirectOnClosClick) {
      shouldReplaceUrlAtUnmount.current = false;
      params.redirectOnClosClick();
    }

    onClose();
  };

  useEffect(() => {
    if (creditsRefillStatusQuery.isSuccess) {
      trackVerticaCreditsNotEnough({
        replenish: creditsRefillStatusQuery.data?.enabled ? 'on' : 'off',
        operation: 'show',
      });
    }

    return () => {
      dispatch(oneClickPaymentReset());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditsRefillStatusQuery.isSuccess]);

  useEffect(() => {
    history.replace(
      `${location.pathname}?${getFunnelSearchString(params.funnel)}`
    );

    return () => {
      if (shouldReplaceUrlAtUnmount.current) {
        history.replace(location.pathname);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldReplaceUrlAtUnmount]);

  return (
    <>
      <ImagedPopup
        image={thumbnailImg}
        icon={<HeartIcon />}
        onClose={handleCloseClick}
      >
        <p
          className={classNames(css.title, {
            [css.titleThemeMePlusYou]: MirrorService.isMePlusYouOrLocalsjoy,
            [css.titleSimpleDates]: MirrorService.isSimpleDates,
          })}
        >
          {t('app:creditsInsufficientPopup.title', {
            currency: creditsCurrency,
          })}
        </p>
        {cardMask && <CardMask mask={cardMask} className={css.cardMask} />}
        <div className={css.replenish}>
          <Checkbox
            checked={creditsRefillStatusQuery.data?.enabled}
            onChange={handleReplenishChecked}
          >
            {t('app:creditsPackages.replenish.title', {
              currency: creditsCurrency,
            })}
          </Checkbox>
        </div>
        <BaseButton
          type={
            MirrorService.isMePlusYouOrLocalsjoy
              ? ButtonTypes.Primary
              : ButtonTypes.Accent
          }
          className={css.button}
          loading={isSubmitting}
          onClick={handlePay}
        >
          {t('app:creditsInsufficientPopup.submitBtn', {
            count: creditPackage?.credits_buy,
            currency: creditsCurrency,
          })}
        </BaseButton>
        <footer className={css.footer}>
          <span className={css.link}>
            {t('app:creditsInsufficientPopup.price', {
              price: packagePrice?.applyCurrency(packagePrice.afterDiscount),
            })}
          </span>
          <BaseButton
            type={ButtonTypes.Transparent}
            className={css.link}
            onClick={handleChangePackage}
            disabled={isSubmitting}
          >
            {t('app:creditsInsufficientPopup.changePackage', {
              currency: creditsCurrency,
            })}
          </BaseButton>
        </footer>
      </ImagedPopup>

      {visiblePaymentFailPopup === PaymentFailPopupType.Default && (
        <DefaultPaymentFailPopup
          buttonText={t('common:tryAnotherCard')}
          onMount={handlePaymentFailPopupMount}
          onClose={handlePaymentFailPopupCloseClick}
          onContinue={handleChangeCreditCard}
        />
      )}
      {(visiblePaymentFailPopup === PaymentFailPopupType.Insufficient ||
        isCardBlocked) && (
        <InsufficientPaymentFailPopup
          buttonText={t('common:tryAnotherCard')}
          onMount={handlePaymentFailPopupMount}
          onClose={handlePaymentFailPopupCloseClick}
          onContinue={handleChangeCreditCard}
        />
      )}
      {isCvvVisible && (
        <CvvPopup
          cardMask={cardMask}
          price={`${packagePrice?.applyCurrency(packagePrice?.afterDiscount)}`}
          isSubmitting={isSubmitting}
          onSubmit={handleResign}
          onClose={handleCloseCvvPopup}
        />
      )}
      {!!verifyUrl && !!orderId && (
        <Payment3dsPopup
          url={verifyUrl}
          tracking={{
            enabled: true,
            orderId,
            orderType: OrderType.Credit,
          }}
        />
      )}
    </>
  );
};
